import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Target, CheckCircle2, XCircle, DollarSign, Calendar as CalIcon } from 'lucide-react';

const STAGE_COLORS = {
  new: 'border-slate-300 bg-slate-50',
  contact_attempted: 'border-blue-300 bg-blue-50',
  contacted: 'border-indigo-300 bg-indigo-50',
  qualified: 'border-violet-300 bg-violet-50',
  appointment_booked: 'border-amber-300 bg-amber-50',
  appointment_attended: 'border-orange-300 bg-orange-50',
  won: 'border-emerald-400 bg-emerald-50',
  lost: 'border-rose-300 bg-rose-50',
};

export default function OutcomesPage() {
  const [stages, setStages] = useState([]);
  const [labels, setLabels] = useState({});
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(null); // {outcome}
  const [saving, setSaving] = useState(false);
  const [edit, setEdit] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get('/outcomes/stages'),
          api.get('/outcomes/mine'),
        ]);
        setStages(s.data.stages);
        setLabels(s.data.labels);
        setOutcomes(o.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openDrawer = (o) => {
    setEdit({
      stage: o.stage,
      qualification_notes: o.qualification_notes || '',
      appointment_at: o.appointment_at || '',
      deal_value: o.deal_value || '',
      lost_reason: o.lost_reason || '',
      note: '',
    });
    setDrawer(o);
  };

  const patch = async (payload) => {
    if (!drawer) return;
    setSaving(true);
    try {
      await api.patch(`/outcomes/${drawer.outcome_id}`, payload);
      // Optimistically refresh
      const o = await api.get('/outcomes/mine');
      setOutcomes(o.data);
      const updated = o.data.find(x => x.outcome_id === drawer.outcome_id);
      if (updated) setDrawer(updated);
      toast.success('Outcome updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const advanceStage = (nextStage) => patch({ stage: nextStage });

  const saveDetails = () => {
    const payload = {};
    if (edit.qualification_notes !== (drawer.qualification_notes || '')) payload.qualification_notes = edit.qualification_notes;
    if (edit.appointment_at !== (drawer.appointment_at || '')) payload.appointment_at = edit.appointment_at || null;
    if (edit.deal_value !== '' && parseFloat(edit.deal_value) !== drawer.deal_value) payload.deal_value = parseFloat(edit.deal_value);
    if (edit.lost_reason !== (drawer.lost_reason || '')) payload.lost_reason = edit.lost_reason;
    if (Object.keys(payload).length === 0) {
      toast.info('No changes to save');
      return;
    }
    patch(payload);
  };

  const grouped = stages.reduce((acc, s) => {
    acc[s] = outcomes.filter(o => o.stage === s);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-md bg-[#0055FF] flex items-center justify-center flex-shrink-0">
            <Target className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#0055FF] font-mono font-medium">Outcome Ledger</div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-gray-900">Sales pipeline</h1>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-8">
          Track every purchased lead from first contact to won/lost. This data powers pricing intelligence and seller quality scoring.
        </p>

        {loading ? (
          <div className="text-center py-16 text-gray-500 text-sm font-mono">Loading outcomes...</div>
        ) : outcomes.length === 0 ? (
          <div className="border border-dashed border-gray-300 rounded-lg p-12 text-center text-gray-500 text-sm" data-testid="outcomes-empty">
            No purchased leads yet. Once you buy a lead it will appear here in the &ldquo;New&rdquo; column.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="outcomes-kanban">
            {stages.map(stage => (
              <div key={stage} className="flex flex-col" data-testid={`kanban-col-${stage}`}>
                <div className={`px-3 py-2 rounded-t-md border-t border-l border-r ${STAGE_COLORS[stage] || 'bg-gray-50 border-gray-300'}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider font-mono text-gray-700 font-semibold">{labels[stage]}</div>
                    <div className="text-xs font-mono text-gray-500">{grouped[stage]?.length || 0}</div>
                  </div>
                </div>
                <div className={`flex-1 border-l border-r border-b rounded-b-md p-2 space-y-2 min-h-[120px] bg-white ${STAGE_COLORS[stage]?.split(' ')[0] || 'border-gray-200'}`}>
                  {(grouped[stage] || []).map(o => (
                    <button
                      key={o.outcome_id}
                      onClick={() => openDrawer(o)}
                      className="w-full text-left bg-white border border-gray-200 hover:border-[#0055FF] rounded p-3 transition"
                      data-testid={`outcome-card-${o.outcome_id}`}
                    >
                      <div className="font-mono text-[10px] text-gray-400 mb-1">{o.lead_id}</div>
                      <div className="font-semibold text-sm text-gray-900">{o.lead?.category || 'Lead'}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{o.lead?.region}</div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                        <span className="text-xs font-mono text-gray-600">${o.lead?.price?.toFixed(0) || 0}</span>
                        {o.deal_value > 0 && (
                          <span className="text-xs font-mono text-emerald-700 font-semibold">Deal ${o.deal_value.toFixed(0)}</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Drawer */}
      <Sheet open={!!drawer} onOpenChange={(v) => !v && setDrawer(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="outcome-drawer">
          {drawer && (
            <>
              <SheetHeader className="mb-4">
                <SheetTitle>{drawer.lead?.category || 'Outcome'}</SheetTitle>
                <div className="text-xs text-gray-500 font-mono">{drawer.lead_id} · txn {drawer.transaction_id}</div>
              </SheetHeader>

              <div className="space-y-6">
                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-mono mb-2">Current stage</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-1 rounded text-xs font-mono font-semibold border ${STAGE_COLORS[drawer.stage]}`} data-testid="drawer-current-stage">
                      {labels[drawer.stage]}
                    </span>
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-mono mb-2">Advance to</div>
                  <Select value={edit.stage} onValueChange={(v) => setEdit(e => ({ ...e, stage: v }))}>
                    <SelectTrigger data-testid="drawer-stage-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {stages.map(s => (
                        <SelectItem key={s} value={s}>{labels[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {edit.stage !== drawer.stage && (
                    <Button className="mt-2 bg-[#0055FF] hover:bg-[#0044CC] text-white w-full" onClick={() => advanceStage(edit.stage)} disabled={saving} data-testid="drawer-advance-btn">
                      Move to {labels[edit.stage]}
                    </Button>
                  )}
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-800 mb-1 block">Qualification notes</label>
                  <Textarea rows={3} value={edit.qualification_notes} onChange={e => setEdit(x => ({ ...x, qualification_notes: e.target.value }))} data-testid="drawer-notes" />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-800 mb-1 block flex items-center gap-1"><CalIcon className="w-3.5 h-3.5" /> Appointment date/time</label>
                  <Input type="datetime-local" value={edit.appointment_at ? edit.appointment_at.slice(0, 16) : ''} onChange={e => setEdit(x => ({ ...x, appointment_at: e.target.value ? new Date(e.target.value).toISOString() : '' }))} data-testid="drawer-appt" />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-800 mb-1 block flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> Deal value (if won)</label>
                  <Input type="number" min="0" step="0.01" value={edit.deal_value} onChange={e => setEdit(x => ({ ...x, deal_value: e.target.value }))} data-testid="drawer-deal-value" />
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-800 mb-1 block">Lost reason (if lost)</label>
                  <Input value={edit.lost_reason} onChange={e => setEdit(x => ({ ...x, lost_reason: e.target.value }))} placeholder="e.g. Wrong fit, price, timing" data-testid="drawer-lost-reason" />
                </div>

                <Button onClick={saveDetails} disabled={saving} className="w-full" data-testid="drawer-save-btn">Save details</Button>

                <div>
                  <div className="text-xs uppercase tracking-wider text-gray-500 font-mono mb-2">Stage history</div>
                  <div className="space-y-1">
                    {(drawer.history || []).map((h, i) => (
                      <div key={i} className="text-xs text-gray-600 flex items-start gap-2">
                        {h.stage === 'won' ? <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5" /> : h.stage === 'lost' ? <XCircle className="w-3 h-3 text-rose-600 mt-0.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-1.5" />}
                        <div>
                          <span className="font-semibold">{labels[h.stage] || h.stage}</span>
                          <span className="ml-2 text-gray-400 font-mono">{new Date(h.at).toLocaleString()}</span>
                          {h.note && <div className="text-gray-500 italic">{h.note}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
